import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AiOrchestratorResult, ChatComponent, HistoryComponent, SkeletonBlockComponent, SplitPaneComponent } from '@components';
import { SplitPaneVerticalComponent } from '@components';
import { ReposFacade } from '@state';
import { IntegrationsService, ReposGitHubService, ToastService } from '@services';
import { applyJsonPatchOps, JsonPatchOp } from '../../utils/json-patch';
import { Subscription } from 'rxjs';
import { ReposWorkspaceComponent } from './repos-workspace.component';

@Component({
  selector: 'app-repos',
  templateUrl: './repos.page.html',
  styleUrls: ['./repos.page.scss'],
  standalone: true,
  imports: [
    RouterModule,
    ChatComponent,
    HistoryComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
    SkeletonBlockComponent,
    ReposWorkspaceComponent,
  ],
})
export class ReposPage implements OnInit, OnDestroy {
  public reposStore = inject(ReposFacade);
  private toast = inject(ToastService);
  private integrations = inject(IntegrationsService);
  private github = inject(ReposGitHubService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private qpSub?: Subscription;
  private loadedForProjectId: string | null = null;
  private readonly pendingInstallKey = 'repos.pendingInstallationId';
  private readonly pendingInstallSourceKey = 'repos.pendingInstallSource';
  private readonly personalGithubInstallationIdKey = 'settings.personalGithubInstallationId';
  private readonly personalGithubAccountLoginKey = 'settings.personalGithubAccountLogin';
  private loadAndApplyInFlight: Promise<void> | null = null;

  public repoSuggestions: any[] = [
    { icon: 'bi bi-git', prompt: 'List open pull requests' },
    { icon: 'bi bi-branch', prompt: 'Show recent commits for frontend' },
    { icon: 'bi bi-cpu', prompt: 'What is the build status of main?' },
    { icon: 'bi bi-plus-circle', prompt: 'Create a new repository for this project' },
    { icon: 'bi bi-lightning', prompt: 'Initialize the repo with README, license, and a starter scaffold' },
  ];

  public repoContext: any = {
    page: 'repos',
    purpose: 'Connect GitHub, create/manage repositories, and review PRs/commits/status',
    outputs: ['Repo selection', 'Repo initialization', 'Branch/PR workflows', 'Status and recent activity'],
  };

  constructor() {
    // Auto-load once the active project becomes available.
    effect(() => {
      const pid = this.reposStore.activeProjectId();
      if (!pid) return;
      if (this.loadedForProjectId === pid) return;
      this.loadedForProjectId = pid;
      // Always sequence: load first, then apply pending install id.
      void this.loadThenApplyPendingInstallationId();
    });
  }

  ngOnInit(): void {
    // Capture GitHub App install redirect parameters (installation_id) and persist them per-project.
    this.qpSub = this.route.queryParamMap.subscribe((m) => {
      const raw = (m.get('installation_id') ?? '').trim();
      if (!raw) return;
      const installationId = Number(raw);
      if (!Number.isFinite(installationId) || installationId <= 0) return;

      // Persist it immediately so we don't lose it if auth/project hydration hasn't completed yet.
      try {
        localStorage.setItem(this.pendingInstallKey, String(installationId));
      } catch {}

      void this.loadThenApplyPendingInstallationId();
    });
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
  }

  get reposOrchestratorContext(): any {
    const settings = this.reposStore.settings();
    const state = this.reposStore.state();
    const definition = this.reposStore.projectDefinition();
    return {
      definition: {
        name: String((definition as any)?.name ?? ''),
        description: String((definition as any)?.description ?? '').slice(0, 500),
        goals: Array.isArray((definition as any)?.goals) ? (definition as any).goals.slice(0, 8) : [],
        features: Array.isArray((definition as any)?.features) ? (definition as any).features.slice(0, 10) : [],
      },
      settings: {
        github: {
          installationId: settings?.github?.installationId ?? null,
          repos: Array.isArray(settings?.github?.repos) ? settings.github.repos.slice(0, 10) : [],
        },
      },
      state: {
        repo: state?.repo ?? null,
        status: state?.status ?? null,
        branches: Array.isArray(state?.branches) ? state.branches.slice(0, 25) : [],
        pulls: Array.isArray(state?.pulls) ? state.pulls.slice(0, 20) : [],
        commits: Array.isArray(state?.commits) ? state.commits.slice(0, 20) : [],
        lastRefreshedAt: state?.lastRefreshedAt ?? null,
      },
    };
  }

  private async loadThenApplyPendingInstallationId(): Promise<void> {
    if (this.loadAndApplyInFlight) return this.loadAndApplyInFlight;
    this.loadAndApplyInFlight = (async () => {
      await this.reposStore.load();
      // If we returned from GitHub install before project selection hydrated, apply now.
      await this.applyPendingInstallationId();
      // Prefer the user's personal GitHub installation when saved, and otherwise fall back to the org install.
      await this.ensureProjectInstallationIfMissing();
    })().finally(() => {
      this.loadAndApplyInFlight = null;
    });
    return this.loadAndApplyInFlight;
  }

  private async applyPendingInstallationId(): Promise<void> {
    const pid = this.reposStore.activeProjectId();
    let returnState = String(this.route.snapshot.queryParamMap.get('state') ?? '').trim().toLowerCase();
    if (!returnState) {
      try {
        returnState = String(localStorage.getItem(this.pendingInstallSourceKey) ?? '').trim().toLowerCase();
      } catch {
        returnState = '';
      }
    }

    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.pendingInstallKey);
    } catch {
      raw = null;
    }
    const installationId = Number(String(raw ?? '').trim());
    if (!Number.isFinite(installationId) || installationId <= 0) return;

    try {
      try {
        const snapshot = await this.integrations.getSnapshot();
        const existingPersonals = Array.isArray((snapshot as any)?.github?.personals) ? (snapshot as any).github.personals : [];
        await this.integrations.updateGithubContext({
          activeInstallationMode: 'personal',
          personal: { installationId },
          personals: [
            ...existingPersonals.map((entry: any) => ({
              installationId: Number(entry?.installationId ?? 0) || undefined,
              accountLogin: String(entry?.accountLogin ?? '').trim() || undefined,
              accountType: String(entry?.accountType ?? '').trim() || undefined,
            })),
            { installationId },
          ],
          activePersonalInstallationId: installationId,
        });
        const resolved = await this.github.getDefaultInstallation('personal');
        const resolvedInstallationId = Number((resolved as any)?.installationId ?? 0);
        const accountLogin = String((resolved as any)?.accountLogin ?? '').trim();
        if (resolvedInstallationId > 0) {
          await this.integrations.updateGithubContext({
            activeInstallationMode: 'personal',
            personal: {
              installationId: resolvedInstallationId,
              accountLogin: accountLogin || undefined,
            },
            personals: [
              ...existingPersonals.map((entry: any) => ({
                installationId: Number(entry?.installationId ?? 0) || undefined,
                accountLogin: String(entry?.accountLogin ?? '').trim() || undefined,
                accountType: String(entry?.accountType ?? '').trim() || undefined,
              })),
              {
                installationId: resolvedInstallationId,
                accountLogin: accountLogin || undefined,
              },
            ],
            activePersonalInstallationId: resolvedInstallationId,
          });
          try {
            localStorage.setItem(this.personalGithubInstallationIdKey, String(resolvedInstallationId));
            if (accountLogin) {
              localStorage.setItem(this.personalGithubAccountLoginKey, accountLogin);
            }
          } catch {}
        }
      } catch {
        // Keep project-level save successful even if user-level integration save fails.
      }
      if (returnState !== 'settings' && pid) {
        await this.reposStore.patchSettings({
          github: {
            installationId,
          },
        });
      }
      this.toast.success('GitHub App connected', returnState === 'settings' ? 'Settings' : 'Repos');
      try {
        localStorage.removeItem(this.pendingInstallKey);
        localStorage.removeItem(this.pendingInstallSourceKey);
      } catch {}

      // Strip params so refresh doesn't re-apply.
      await this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { installation_id: null, setup_action: null, state: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      if (returnState === 'settings') {
        await this.router.navigate(['/settings']);
      }
    } catch {
      // ignore; store has its own error handling
    }
  }

  private async ensureProjectInstallationIfMissing(): Promise<void> {
    const pid = this.reposStore.activeProjectId();
    if (!pid) return;
    const currentInstallationId = this.reposStore.settings().github.installationId;
    if (typeof currentInstallationId === 'number' && Number.isFinite(currentInstallationId) && currentInstallationId > 0) {
      return;
    }

    try {
      let res: { installationId: number; accountLogin: string } | null = null;
      let mode: 'personal' | 'business' = 'business';
      try {
        res = await this.github.getDefaultInstallation('personal');
        mode = 'personal';
      } catch {
        res = await this.github.getDefaultInstallation('business');
      }
      const installationId = Number((res as any)?.installationId ?? 0);
      if (!Number.isFinite(installationId) || installationId <= 0) return;
      const accountLogin = String((res as any)?.accountLogin ?? '').trim() || undefined;

      await this.reposStore.patchSettings({
        github: {
          installationId,
        },
      });

      try {
        await this.integrations.updateGithubContext({
          activeInstallationMode: mode,
          ...(mode === 'personal'
            ? {
                personal: {
                  installationId,
                  accountLogin,
                },
              }
            : {
                business: {
                  installationId,
                  accountLogin,
                },
              }),
        });
        if (mode === 'personal') {
          try {
            localStorage.setItem(this.personalGithubInstallationIdKey, String(installationId));
            if (accountLogin) {
              localStorage.setItem(this.personalGithubAccountLoginKey, accountLogin);
            }
          } catch {}
        }
      } catch {
        // Keep project-level hydration successful even if user-level integration save fails.
      }
    } catch {
      // If the org/business installation is not available, leave the manual GitHub connect flow visible.
    }
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.reposStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.reposStore.logChatEvent(msg.text, msg.role);
  }

  async onAiResult(result: AiOrchestratorResult) {
    if (!result || String(result.page ?? '').toLowerCase() !== 'repos') return;
    const opsRaw = (Array.isArray(result.patches) ? result.patches : []) as JsonPatchOp[];
    if (opsRaw.length === 0) return;

    const normalizePath = (p: string): string => {
      const path = String(p ?? '');
      if (!path) return path;
      if (path.startsWith('/settings/') || path === '/settings') return path;
      if (path.startsWith('/state/') || path === '/state') return path;

      // Forgiving aliases the agent may emit.
      if (path === '/installationId') return '/settings/github/installationId';
      if (path.startsWith('/github/')) return `/settings${path}`;

      if (path === '/repo') return '/state/repo';
      if (path.startsWith('/repo/')) return `/state${path}`;
      if (path === '/pulls' || path.startsWith('/pulls/')) return `/state${path}`;
      if (path === '/commits' || path.startsWith('/commits/')) return `/state${path}`;
      if (path === '/branches' || path.startsWith('/branches/')) return `/state${path}`;
      if (path === '/status' || path.startsWith('/status/')) return `/state${path}`;
      if (path === '/repos' || path.startsWith('/repos/')) return `/state${path}`;
      if (path === '/lastRefreshedAt') return '/state/lastRefreshedAt';

      return path;
    };

    const ops = opsRaw.map((op: any) => ({ ...op, path: normalizePath(String(op?.path ?? '')) })) as JsonPatchOp[];

    try {
      const base = { settings: this.reposStore.settings(), state: this.reposStore.state() };
      const next = applyJsonPatchOps<typeof base>(base, ops);
      await this.reposStore.saveSettings(next.settings);
      await this.reposStore.saveState(next.state);
      this.toast.success('AI updated repos workspace', 'Repos');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to apply AI updates', 'Repos');
    }
  }
}